import React, { FunctionComponent, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';

import './assets/css/main.scss';

import { Security, useSecurity } from '@sms/security-plasma-security-auth-sdk-react';

import { App, Result, ThemeDimensions, User } from '@sms/plasma-ui';
import logo from './assets/img/logo.svg';
import app from './config/app.json';
import translations from './config/i18n';
import pages from './pages';
import CentralizedTextPage from './components/CentralizedTextPage/CentralizedTextPage';
import IFrameContainer from './components/IFramePage/IFrameContainer';

const MainApp: FunctionComponent = () => {
  const security = useSecurity();

  const [hasTriedSignin, setHasTriedSignin] = React.useState(false);

  // const handleLanguageChange = useCallback((language: string) => {
  //   localStorage.setItem('@sms:language', language);
  // }, []);

  const handleLogout = useCallback(() => {
    security.signoutRedirect();
  }, []);

  const language = 'pt_BR';
  localStorage.setItem('@sms:language', language);

  const handleThemeChange = useCallback((theme: ThemeDimensions) => {
    localStorage.setItem('@sms:theme', JSON.stringify(theme));
  }, []);

  const user = useMemo(
    () =>
      ({
        id: security.user?.profile.sub,
        firstName: security.user?.profile.given_name,
        lastName: security.user?.profile.family_name,
        email: security.user?.profile.email,
        username: security.user?.profile.preferred_username,
      }) as User,
    [security.user],
  );

  const theme = useMemo(() => {
    const themeString = localStorage.getItem('@sms:theme');
    return themeString ? (JSON.parse(themeString) as ThemeDimensions) : undefined;
  }, []);

  // automatically sign-in
  React.useEffect(() => {
    if (!security.isAuthenticated && !security.activeNavigator && !security.isLoading && !hasTriedSignin) {
      security.signinRedirect();
      setHasTriedSignin(true);
    }
  }, [security, hasTriedSignin]);

  if (!security.isAuthenticated && security.isLoading)
    return <CentralizedTextPage textKey="Signing you in/out of the application..." />;

  if (!security.isAuthenticated) return <CentralizedTextPage textKey="Unable to log in" />;

  if (!security.permissions.size && security.isLoading)
    return <CentralizedTextPage textKey="Getting permissions..." />;

  return (
    <App
      code={app.code}
      isAuthenticated
      user={user}
      routerConfig={{ pages, launchpadRoutePath: `/${app.code}` }}
      headerConfig={{ logo, title: app.title }}
      profileAvatarConfig={{ onLogout: handleLogout }}
      language={language}
      // onLanguageChange={handleLanguageChange}
      availableLanguages={['pt_BR']}
      theme={theme}
      onThemeChange={handleThemeChange}
      translations={translations}
      hasAuthorization={(event) => {
        return security.hasAuthorization(event);
      }}
      permissions={Array.from(security.permissions)}
    />
  );
};

ReactDOM.render(
  <>
    <Security
      unauthorizedRedirectPath="/login"
      unauthorizedAppComponent={<Result status="403" />}
      unauthorizedRouteComponent={<Result status="403" />}
      accessTokenExpiringNotificationTimeInSeconds={30}
    >
      <MainApp />
      <IFrameContainer />
    </Security>
  </>,
  document.getElementById('root'),
);
